import { h, Fragment } from 'preact';
import { useContext, useRef } from 'preact/hooks';
import classNames from 'classnames';
import ChatIcon from 'widget_main/components/ChatIcon';
import {
  SettingsContext,
  ChatAvailabilityContext,
  ConversationsContext,
  UnreadCountsContext,
  CoreStoreRevisionContext,
} from 'widget_main/store';
import { getHTMLFromMarkdown } from 'widget_main/components/Markdown/helpers';
import Markdown from 'widget_main/components/Markdown/Markdown';
import { useTranslations } from 'i18n/hooks';
import * as chatHistoryStatusFilter from 'globals/chatHistoryStatusFilter';
import { getStore } from 'globals/coreStore';
import historyFilterIcon from 'widget_main/globals/assets/historyFilterIcon.png';

import styles from './conversationsHeader.scss';
import messages from './messages';

const ConversationsHeader = () => {
  const historyFilterSelectRef = useRef<HTMLSelectElement | null>(null);
  const chatAvailability = useContext(ChatAvailabilityContext);
  const chatSettings = useContext(SettingsContext);
  const conversations = useContext(ConversationsContext);
  const unreadCounts = useContext(UnreadCountsContext);
  useContext(CoreStoreRevisionContext);
  const greeting = chatSettings?.greeting;
  const translations = useTranslations(messages);

  const {
    enableHistoryStatusFilter,
    historyStatusFilter,
    customHistoryFilter,
  } = getStore();
  const hasConversations = Object.keys(conversations.data).length > 0;
  const showHistoryFilterChrome = enableHistoryStatusFilter && hasConversations;

  const renderSubText = () => {
    if (chatAvailability === 'online') {
      return (
        <Fragment>
          {greeting && (
            <span class={styles.greeting}>
              <Markdown
                markdown={getHTMLFromMarkdown(greeting, { breaks: true })}
              />
            </span>
          )}
        </Fragment>
      );
    }

    return (
      <span className={styles.greeting}>
        <Markdown
          markdown={getHTMLFromMarkdown(chatSettings?.offhoursMessage, {
            breaks: true,
          })}
        />
      </span>
    );
  };

  const renderFilterRow = () => {
    if (!showHistoryFilterChrome) {
      return null;
    }

    const summaryCounts = chatHistoryStatusFilter.getHistoryFilterSummaryCounts(
      {
        conversations: Object.values(conversations.data),
        noHistory: !!chatSettings.noHistory,
        unreadCounts: unreadCounts.conversations,
        statusFilter: historyStatusFilter,
        customClauses: customHistoryFilter,
      },
    );

    const summaryText = translations.filterSummary
      .replace('{visible}', String(summaryCounts.visible))
      .replace('{total}', String(summaryCounts.total));
    const showSummary = summaryCounts.visible !== summaryCounts.total;
    const filterLabels: Record<
      chatHistoryStatusFilter.HistoryStatusFilterValue,
      string
    > = {
      all: translations.filterAll,
      open: translations.filterOpen,
      closed: translations.filterClosed,
      requiresAttention: translations.filterRequiresAttention,
    };
    const statusFilters: chatHistoryStatusFilter.HistoryStatusFilterValue[] = [
      'all',
      'open',
      'closed',
      'requiresAttention',
    ];
    const statusFilterLabels = statusFilters.reduce<
      Record<chatHistoryStatusFilter.HistoryStatusFilterValue, string>
    >((acc, statusFilter) => {
      const { visible } = chatHistoryStatusFilter.getHistoryFilterSummaryCounts(
        {
          conversations: Object.values(conversations.data),
          noHistory: !!chatSettings.noHistory,
          unreadCounts: unreadCounts.conversations,
          statusFilter,
          customClauses: customHistoryFilter,
        },
      );
      acc[statusFilter] =
        visible >= 1
          ? `${filterLabels[statusFilter]} (${visible})`
          : filterLabels[statusFilter];
      return acc;
    }, {} as Record<chatHistoryStatusFilter.HistoryStatusFilterValue, string>);

    const openHistoryFilterSelect = () => {
      const select = historyFilterSelectRef.current;
      if (!select) {
        return;
      }
      select.focus();
      const selectWithPicker = select as HTMLSelectElement & {
        showPicker?: () => void;
      };
      if (typeof selectWithPicker.showPicker === 'function') {
        selectWithPicker.showPicker();
      }
    };

    return (
      <div class={styles.filterRow} data-kt="conversationHistoryFilterRow">
        {showSummary && (
          <span
            class={styles.filterSummary}
            data-kt="conversationHistoryFilterSummary"
          >
            {summaryText}
          </span>
        )}
        <span
          class={styles.filterIcon}
          data-kt="conversationHistoryFilterIcon"
          data-testid="conversationHistoryFilterIcon"
          style={{
            WebkitMaskImage: `url(${historyFilterIcon})`,
            maskImage: `url(${historyFilterIcon})`,
          }}
          aria-hidden="true"
          onClick={openHistoryFilterSelect}
        />
        <span class={styles.filterSelectWrapper}>
          <span class={styles.filterSelectLabel} aria-hidden="true">
            {filterLabels[historyStatusFilter]}
          </span>
          <select
            ref={historyFilterSelectRef}
            className={styles.filterSelect}
            aria-label={translations.filterAriaLabel}
            data-kt="conversationHistoryStatusFilter"
            value={historyStatusFilter}
            onChange={(e) => {
              const target = e.currentTarget as HTMLSelectElement;
              conversations.setHistoryStatusFilter(
                target.value as chatHistoryStatusFilter.HistoryStatusFilterValue,
              );
            }}
          >
            <option value="all">{statusFilterLabels.all}</option>
            <option value="open">{statusFilterLabels.open}</option>
            <option value="closed">{statusFilterLabels.closed}</option>
            <option value="requiresAttention">
              {statusFilterLabels.requiresAttention}
            </option>
          </select>
        </span>
      </div>
    );
  };

  return (
    <div class={styles.conversationsHeader}>
      <div
        class={classNames(styles.content, {
          [styles.contentWithHistoryFilter]: showHistoryFilterChrome,
        })}
      >
        <ChatIcon
          className={styles.chatIconWrapper}
          size={{ width: 50, height: 50 }}
        />
        <div className={styles.text}>
          <h1 class={styles.teamName}>{chatSettings?.teamName}</h1>
          {renderSubText()}
        </div>
        {renderFilterRow()}
      </div>
    </div>
  );
};

export default ConversationsHeader;
